"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Play, Film, Calendar, User, Image, Loader, Search, Filter, Clock, Heart, MessageSquare, Download, X, Volume2, VolumeX, Pause, MoreVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
  
  @media (max-width: 420px) {
    gap: 12px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 420px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
  color: ${props => props.theme.textPrimary};
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 420px) {
    font-size: 18px;
  }
`

const Tabs = styled.div`
  display: flex;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  overflow-x: auto;
  
  @media (max-width: 768px) {
    gap: 0;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${props => props.theme.border};
      border-radius: 2px;
    }
  }
`

const Tab = styled.button`
  padding: 12px 24px;
  background-color: ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
  color: ${(props) => (props.$active ? "white" : props.theme.textPrimary)};
  border: none;
  cursor: pointer;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  white-space: nowrap;
  min-width: fit-content;
  
  &:hover {
    background-color: ${(props) => (props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground)};
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
  
  @media (max-width: 420px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 420px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`

const MediaCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-4px);
  }
`

const MediaThumbnail = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: ${props => props.theme.border};
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    height: 48px;
    background-color: ${props => `rgba(${parseInt(props.theme.buttonPrimary.slice(1, 3), 16)}, ${parseInt(props.theme.buttonPrimary.slice(3, 5), 16)}, ${parseInt(props.theme.buttonPrimary.slice(5, 7), 16)}, 0.8)`};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  &:hover .play-button {
    opacity: 1;
  }
  
  .tag {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: ${props => `rgba(${parseInt(props.theme.buttonPrimary.slice(1, 3), 16)}, ${parseInt(props.theme.buttonPrimary.slice(3, 5), 16)}, ${parseInt(props.theme.buttonPrimary.slice(5, 7), 16)}, 0.8)`};
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`

const MediaInfo = styled.div`
  padding: 12px;
  
  @media (max-width: 420px) {
    padding: 10px;
  }
`

const MediaTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  min-height: 2.6em;
  color: ${props => props.theme.textPrimary};
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 420px) {
    font-size: 14px;
    -webkit-line-clamp: 3;
    min-height: 3.9em;
    margin: 0 0 6px 0;
  }
`

const MediaMeta = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  gap: 12px;
  flex-wrap: wrap;
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  @media (max-width: 768px) {
    gap: 10px;
  }
  
  @media (max-width: 420px) {
    gap: 8px;
    font-size: 11px;
    
    .meta-item {
      gap: 3px;
    }
  }
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.textSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.error};
  border-radius: 8px;
`

// 검색 및 필터 스타일
const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
  
  @media (max-width: 420px) {
    margin-bottom: 16px;
  }
`

const SearchBar = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: ${props => props.theme.shadow};
  
  input {
    border: none;
    flex: 1;
    margin-left: 8px;
    outline: none;
    font-size: 14px;
    background: transparent;
    color: ${props => props.theme.textPrimary};
    
    &::placeholder {
      color: ${props => props.theme.textMuted};
    }
  }
`

const FilterDropdown = styled.div`
  position: relative;
  display: inline-block;
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.border};
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const FilterMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 8px;
  min-width: 200px;
  z-index: 100;
  margin-top: 4px;
`

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  input {
    margin: 0;
  }
`

// 비디오 플레이어 모달 스타일
const VideoModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 420px) {
    padding: 8px;
  }
`

const VideoModalContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 8px;
  }
  
  @media (max-width: 420px) {
    max-width: 98vw;
    max-height: 98vh;
    border-radius: 6px;
  }
`

const VideoPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16/9;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s;
`

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  
  .progress {
    height: 100%;
    background-color: ${props => props.theme.buttonPrimary};
    border-radius: 2px;
    transition: width 0.1s;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`

const VideoInfo = styled.div`
  padding: 20px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 420px) {
    padding: 12px;
  }
`

const VideoTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 420px) {
    font-size: 14px;
    margin: 0 0 8px 0;
  }
`

const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  flex-wrap: wrap;
  
  .author {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    
    &:hover {
      color: ${props => props.theme.buttonPrimary};
    }
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    font-size: 13px;
  }
  
  @media (max-width: 420px) {
    gap: 8px;
    font-size: 12px;
    margin-bottom: 8px;
  }
`

const VideoActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 420px) {
    gap: 8px;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &.liked {
    background-color: ${props => props.theme.cardBackground};
    color: ${props => props.theme.error};
    border-color: ${props => props.theme.error};
  }
  
  @media (max-width: 420px) {
    padding: 6px 10px;
    font-size: 11px;
    gap: 4px;
  }
`

export default function Media() {
  const { isLoggedIn, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [filters, setFilters] = useState({
    sortBy: 'newest',
    duration: 'all',
    author: 'all'
  });

  // 포스트 목록 가져오기 함수
  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/post/list', {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log("포스트 목록 로드 성공:", data.posts);
        // 이미지가 있는 포스트만 필터링
        const postsWithImages = data.posts.filter(post => 
          post.images && post.images.length > 0
        );
        setPosts(postsWithImages);
      } else {
        console.error("포스트 목록 로드 실패:", data.message);
        setError(data.message || "포스트를 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("포스트 목록 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 포스트 목록 가져오기
  useEffect(() => {
    fetchPosts();
  }, []);

  // 태그 기반으로 미디어 타입 결정
  const getMediaType = (tags) => {
    if (!tags || !tags.length) return "기타";
    
    if (tags.includes("동영상") || tags.includes("video")) return "video";
    if (tags.includes("튜토리얼") || tags.includes("tutorial")) return "tutorial";
    if (tags.includes("브이로그") || tags.includes("vlog")) return "vlog";
    if (tags.includes("이벤트") || tags.includes("event")) return "event";
    
    return "기타";
  };

  // 게시물에 태그 기반으로 미디어 타입 추가
  const mediaItems = posts.map(post => {
    const mediaType = getMediaType(post.hashtags || post.tags);
    const hasVideo = post.images && post.images.some(img => 
      img.url && (img.url.includes('.mp4') || img.url.includes('.mov') || img.url.includes('.avi'))
    );
    
    return {
      ...post,
      mediaType,
      hasVideo,
      // 콘텐츠의 첫 50자를 타이틀로 사용
      title: post.content ? (post.content.length > 50 ? post.content.substring(0, 50) + '...' : post.content) : "제목 없음",
      thumbnail: post.images && post.images.length > 0 ? post.images[0].url : null,
      videoUrl: hasVideo ? post.images.find(img => 
        img.url && (img.url.includes('.mp4') || img.url.includes('.mov') || img.url.includes('.avi'))
      )?.url : null
    };
  });

  // 필터링 로직
  const getFilteredMedia = () => {
    let filtered = mediaItems;

    // 탭 필터
    if (activeTab !== "all") {
      filtered = filtered.filter(item => item.mediaType === activeTab);
    }

    // 검색 필터
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.author_username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.hashtags && item.hashtags.some(tag => 
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }

    // 작성자 필터
    if (filters.author !== 'all') {
      if (filters.author === 'me') {
        filtered = filtered.filter(item => item.author_id === user?.id);
      }
    }

    // 정렬
    switch (filters.sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'popular':
        filtered.sort((a, b) => (b.like_count || 0) - (a.like_count || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredMedia = getFilteredMedia();

  // 비디오 플레이어 핸들러
  const openVideoModal = (item) => {
    setSelectedVideo(item);
    setIsPlaying(false);
    setProgress(0);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsPlaying(false);
    setProgress(0);
  };

  const togglePlay = () => {
    const video = document.querySelector('.video-player');
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    const video = document.querySelector('.video-player');
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleProgress = (e) => {
    const video = document.querySelector('.video-player');
    if (video) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      video.currentTime = pos * video.duration;
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Container>
      <Header>
        <Title theme={theme}>미디어</Title>
      </Header>

      {/* 검색 및 필터 섹션 */}
      <SearchSection>
        <SearchBar theme={theme}>
          <Search size={20} color={theme.textMuted} />
          <input
            type="text"
            placeholder="미디어 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        
        <FilterDropdown>
          <FilterButton theme={theme} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            필터
          </FilterButton>
          {showFilters && (
            <FilterMenu theme={theme}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                정렬 기준
              </div>
              <FilterOption theme={theme}>
                <input 
                  type="radio" 
                  name="sortBy" 
                  value="newest"
                  checked={filters.sortBy === 'newest'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                최신순
              </FilterOption>
              <FilterOption theme={theme}>
                <input 
                  type="radio" 
                  name="sortBy" 
                  value="oldest"
                  checked={filters.sortBy === 'oldest'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                오래된 순
              </FilterOption>
              <FilterOption theme={theme}>
                <input 
                  type="radio" 
                  name="sortBy" 
                  value="popular"
                  checked={filters.sortBy === 'popular'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                인기순
              </FilterOption>
              
              {isLoggedIn && (
                <>
                  <hr style={{ margin: '8px 0', border: 'none', borderTop: `1px solid ${theme.border}` }} />
                  <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                    작성자
                  </div>
                  <FilterOption theme={theme}>
                    <input 
                      type="radio" 
                      name="author" 
                      value="all"
                      checked={filters.author === 'all'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    모든 사용자
                  </FilterOption>
                  <FilterOption theme={theme}>
                    <input 
                      type="radio" 
                      name="author" 
                      value="me"
                      checked={filters.author === 'me'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    내 미디어만
                  </FilterOption>
                </>
              )}
            </FilterMenu>
          )}
        </FilterDropdown>
      </SearchSection>

      <Tabs theme={theme}>
        <Tab theme={theme} $active={activeTab === "all"} onClick={() => setActiveTab("all")}>
          전체
        </Tab>
        <Tab theme={theme} $active={activeTab === "video"} onClick={() => setActiveTab("video")}>
          비디오
        </Tab>
        <Tab theme={theme} $active={activeTab === "tutorial"} onClick={() => setActiveTab("tutorial")}>
          튜토리얼
        </Tab>
        <Tab theme={theme} $active={activeTab === "vlog"} onClick={() => setActiveTab("vlog")}>
          브이로그
        </Tab>
        <Tab theme={theme} $active={activeTab === "event"} onClick={() => setActiveTab("event")}>
          이벤트
        </Tab>
      </Tabs>

      {loading ? (
        <LoadingIndicator theme={theme}>
          <Loader size={32} className="animate-spin" />
          <div>미디어를 불러오는 중...</div>
        </LoadingIndicator>
      ) : error ? (
        <ErrorMessage theme={theme}>{error}</ErrorMessage>
      ) : filteredMedia.length === 0 ? (
        <LoadingIndicator theme={theme}>
          <Image size={32} />
          <div>표시할 미디어가 없습니다</div>
        </LoadingIndicator>
      ) : (
        <MediaGrid>
          {filteredMedia.map((item) => (
            <MediaCard 
              key={item.id}
              theme={theme}
              onClick={() => {
                if (item.hasVideo) {
                  openVideoModal(item);
                } else {
                  // 이미지만 있는 경우 새 창에서 열기
                  window.open(item.thumbnail, '_blank');
                }
              }}
            >
              <MediaThumbnail theme={theme}>
                <img 
                  src={item.thumbnail || "/placeholder.svg"} 
                  alt={item.title}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/placeholder.svg?height=200&width=300";
                  }}
                />
                <div className="play-button">
                  {item.hasVideo ? <Play size={24} fill="white" /> : <Image size={24} />}
                </div>
                {item.mediaType && item.mediaType !== "기타" && (
                  <div className="tag">{item.mediaType}</div>
                )}
                {item.images && item.images.length > 1 && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px'
                  }}>
                    +{item.images.length - 1}
                  </div>
                )}
              </MediaThumbnail>
              <MediaInfo>
                <MediaTitle theme={theme}>{item.title}</MediaTitle>
                <MediaMeta theme={theme}>
                  <div className="meta-item">
                    <Calendar size={12} />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                  <div 
                    className="meta-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/profile/${item.author_username}`);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <User size={12} />
                    <span>{item.author_username}</span>
                  </div>
                  {(item.like_count > 0 || item.comment_count > 0) && (
                    <>
                      {item.like_count > 0 && (
                        <div className="meta-item">
                          <Heart size={12} />
                          <span>{item.like_count}</span>
                        </div>
                      )}
                      {item.comment_count > 0 && (
                        <div className="meta-item">
                          <MessageSquare size={12} />
                          <span>{item.comment_count}</span>
                        </div>
                      )}
                    </>
                  )}
                </MediaMeta>
              </MediaInfo>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {/* 비디오 모달 */}
      {selectedVideo && (
        <VideoModalOverlay onClick={closeVideoModal}>
          <VideoModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeVideoModal}>
              <X size={20} />
            </CloseButton>
            
            <VideoPlayerContainer
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
            >
              {selectedVideo.videoUrl ? (
                <video
                  className="video-player"
                  src={selectedVideo.videoUrl}
                  poster={selectedVideo.thumbnail}
                  onTimeUpdate={(e) => {
                    const video = e.target;
                    setProgress((video.currentTime / video.duration) * 100);
                  }}
                  onEnded={() => setIsPlaying(false)}
                  onError={() => console.log('Video load error')}
                />
              ) : (
                <img 
                  src={selectedVideo.thumbnail}
                  alt={selectedVideo.title}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
              
              {selectedVideo.videoUrl && (
                <VideoControls $visible={showControls}>
                  <ControlButton onClick={togglePlay}>
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </ControlButton>
                  
                  <ControlButton onClick={toggleMute}>
                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </ControlButton>
                  
                  <ProgressBar theme={theme} onClick={handleProgress}>
                    <div className="progress" style={{ width: `${progress}%` }} />
                  </ProgressBar>
                  
                  <ControlButton 
                    onClick={() => {
                      const video = document.querySelector('.video-player');
                      if (video.requestFullscreen) {
                        video.requestFullscreen();
                      }
                    }}
                  >
                    <MoreVertical size={20} />
                  </ControlButton>
                </VideoControls>
              )}
            </VideoPlayerContainer>
            
            <VideoInfo theme={theme}>
              <VideoTitle>{selectedVideo.title}</VideoTitle>
              <VideoMeta theme={theme}>
                <div 
                  className="author"
                  onClick={() => {
                    closeVideoModal();
                    navigate(`/profile/${selectedVideo.author_username}`);
                  }}
                >
                  <User size={16} />
                  {selectedVideo.author_username}
                </div>
                <div>
                  <Calendar size={16} />
                  {formatDate(selectedVideo.created_at)}
                </div>
                <div>
                  <Heart size={16} />
                  {selectedVideo.like_count || 0} 좋아요
                </div>
                <div>
                  <MessageSquare size={16} />
                  {selectedVideo.comment_count || 0} 댓글
                </div>
              </VideoMeta>
              
              {selectedVideo.content && selectedVideo.content !== selectedVideo.title && (
                <div style={{ 
                  marginTop: '12px', 
                  fontSize: '14px', 
                  lineHeight: '1.5',
                  color: theme.textPrimary
                }}>
                  {selectedVideo.content}
                </div>
              )}
              
              {selectedVideo.hashtags && selectedVideo.hashtags.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px', 
                  marginTop: '12px' 
                }}>
                  {selectedVideo.hashtags.map((tag, index) => (
                    <span 
                      key={index} 
                      style={{
                        padding: '4px 8px',
                        backgroundColor: theme.borderLight,
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: theme.textSecondary
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              
              <VideoActions>
                <ActionButton theme={theme}>
                  <Heart size={16} />
                  좋아요
                </ActionButton>
                <ActionButton theme={theme}>
                  <MessageSquare size={16} />
                  댓글
                </ActionButton>
                {selectedVideo.videoUrl && (
                  <ActionButton theme={theme} 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedVideo.videoUrl;
                      link.download = `video_${selectedVideo.id}.mp4`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download size={16} />
                    다운로드
                  </ActionButton>
                )}
              </VideoActions>
            </VideoInfo>
          </VideoModalContainer>
        </VideoModalOverlay>
      )}
    </Container>
  );
}